<template>
    <div id="payment-form">
        <h3 class="section-title">Finalize Payment</h3>
        <div id="payment-element">
        </div>
        <button id="submit" @click="handleSubmit" :disabled="isLoading">
            <span v-if="isLoading">Processing...</span>
            <span v-else>Pay Now</span>
        </button>
        <div v-if="message" id="payment-message">{{ message }}</div>
    </div>
</template>

<script>

import { loadStripe } from '@stripe/stripe-js';

const stripePublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(stripePublishableKey);

const returnUrl = window.location.origin + '/services?payment_intent_result=true';

export default {
    name: 'StripePaymentForm',
    props: ['clientSecret'],
    emits: ['payment-success'],
    data() {
        return {
            stripe: null,
            elements: null,
            message: '',
            isLoading: false,
            
        };
    },
    async mounted() {
        // Initialize Stripe
        if (!stripePublishableKey) {
            console.error("Stripe Publishable Key is missing! Check your .env file.");
            this.message = "Configuration error: Missing Payment Key.";
            return;
        }


        this.stripe = await stripePromise;

        // Create Stripe Elements instance
        // Injecting appearance settings here allows Stripe Elements to inherit some styles
        this.elements = this.stripe.elements({ 
            clientSecret: this.clientSecret,
            appearance: {
                theme: 'stripe',
                variables: {
                    colorPrimary: '#4F46E5', // Indigo
                    colorText: '#333',
                    colorDanger: '#EF4444',
                    fontFamily: 'Inter, sans-serif',
                    fontSizeBase: '16px',
                    spacingUnit: '4px',
                },
                rules: {
                    '.Label': {
                        fontWeight: '600',
                        marginBottom: '8px',
                    },
                    '.Input': {
                        padding: '12px 16px',
                        borderRadius: '6px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        border: '1px solid #D1D5DB',
                    },
                    '.Input:focus': {
                        borderColor: '#4F46E5',
                    },
                }
            }
        });

        // Create and mount the Payment Element (the form)
        const paymentElement = this.elements.create('payment');
        paymentElement.mount('#payment-element');
    },
    methods: {

        async handleSubmit() {
            if (!this.stripe || !this.elements) return;

            this.isLoading = true;
            this.message = '';

            // 1. Confirm the payment on the client side
            const { error, paymentIntent } = await this.stripe.confirmPayment({ //Capture paymentIntent 
                elements: this.elements,
                confirmParams: {
                    return_url: returnUrl,
                },
                redirect: 'if_required' 
            });

            if (error) {
                // This error occurs if card is declined, invalid data, 
                this.message = error.message;
                console.log("Stripe error:",this.message)
            } 
            // Check if the paymentIntent was returned and succeeded (for non-redirect flows)
            else if (paymentIntent && paymentIntent.status === 'succeeded') { 
                this.message = 'Payment Succeeded!';
                console.log("Stripe Instant Success:",this.message)
                this.$emit('payment-success');
            }
            // Handle status for redirect flows (they will use the return_url)
            else {
                // This block handles other statuses like 'requires_action' or 'processing'
                // If 'redirect: if_required' was used, Stripe might have initiated a redirect, 
                // or the status might be 'processing'.
                const intentStatus = paymentIntent ? paymentIntent.status : 'Unknown';
                this.message = `Payment processing status: ${intentStatus}. Waiting for final confirmation`;
            }
            
            this.isLoading = false;
           
        }
    }
};
</script>

<style scoped>


/* Base Container Styling (Handles centering and max width) */
#payment-form {
    max-width: 500px;
    margin: 40px auto;
    padding: 20px;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    font-family: 'Inter', sans-serif;
    transition: all 0.3s ease-in-out;
}

/* Ensure padding is adequate on small screens */
@media (max-width: 600px) {
    #payment-form {
        margin: 20px 10px;
        padding: 15px;
    }
}

/* 2. Title Styling */
.section-title {
    font-size: 1.8rem;
    color: #1F2937; /* Dark text */
    margin-bottom: 20px;
    font-weight: 700;
    text-align: center;
    border-bottom: 2px solid #E5E7EB;
    padding-bottom: 10px;
}

/* Stripe Payment Element Container */
#payment-element {
    margin-bottom: 25px;
    padding: 10px 0;
}

/* Submit Button Styling */
#submit {
    width: 100%;
    padding: 14px;
    background-color: #4F46E5; /* Indigo */
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s, transform 0.1s;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    letter-spacing: 0.5px;
}

#submit:hover:not(:disabled) {
    background-color: #4338CA; /* Darker Indigo */
    transform: translateY(-1px);
}

#submit:disabled {
    background-color: #A5B4FC;
    cursor: not-allowed;
    box-shadow: none;
}

/* Message Display */
#payment-message {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
    text-align: center;
    font-weight: 500;
    font-size: 0.95rem;
    
    color: #155724;
    background-color: #D4EDDA;
    border: 1px solid #C3E6CB;
}

.error #payment-message {
    color: #721C24;
    background-color: #F8D7DA;
    border: 1px solid #F5C6CB;
}

</style>